import { getLeadCountsByBusinessTypes } from '@/services/firebase/firestore';
import { useCallback, useEffect, useState } from 'react';

export interface UseLeadCountsResult {
  counts: Record<string, number>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Returns live per-business-type lead counts via Firestore count() aggregation.
 * Fetches once whenever the set of slugs changes; call refresh() after a
 * mutation (add/delete) to re-fetch.
 */
export const useLeadCounts = (slugs: string[]): UseLeadCountsResult => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stabilise dep — slug *content*, not array identity
  const slugsKey = slugs.join(',');

  const refresh = useCallback(async () => {
    if (!slugsKey) {
      setCounts({});
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getLeadCountsByBusinessTypes(slugsKey.split(','));
      setCounts(result);
    } catch (err) {
      console.error('Failed to fetch lead counts:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [slugsKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { counts, loading, error, refresh };
};
